import React, { useState } from "react";
import api from "@/lib/api";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: "📸", needs: ["META_LONG_LIVED_TOKEN", "IG_BUSINESS_ACCOUNT_ID"], requiresMedia: true },
  { id: "facebook",  label: "Facebook",  icon: "👍", needs: ["META_LONG_LIVED_TOKEN", "FB_PAGE_ID"],            requiresMedia: false },
  { id: "tiktok",    label: "TikTok",    icon: "🎵", needs: ["TIKTOK_CLIENT_KEY", "TIKTOK_ACCESS_TOKEN"],       requiresMedia: true },
];

/**
 * SocialComposer — visual composer for posting to IG / FB / TikTok via the
 * `post_social` action. Each platform shows its required env-key checklist;
 * if anything is missing, the FIRE button is disabled and a "Open Settings"
 * shortcut is offered.
 *
 * Used as a stage panel (stage type "social_composer"). Voice trigger:
 * "open the social composer", "post to instagram", "post to all socials".
 */
export default function SocialComposer({ dismiss }) {
  const [platform, setPlatform] = useState("instagram");
  const [caption, setCaption] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const active = PLATFORMS.find((p) => p.id === platform);

  const fire = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/jarvis/chat", {
        message: `post to ${platform} with caption '${caption}'${mediaUrl ? ` and media ${mediaUrl}` : ""}`,
        intent_hint: "post_social",
      });
      const res = r.data?.action?.result || r.data;
      setResult(res);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="social-composer" style={{ padding: 20, color: "var(--rc-text)", fontFamily: "var(--rc-font-display)", minWidth: 480 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: "var(--rc-gold)", marginBottom: 6 }}>// SOCIAL COMPOSER</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>POST TO {active?.label.toUpperCase()}</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            data-testid={`social-tab-${p.id}`}
            onClick={() => setPlatform(p.id)}
            style={{
              flex: 1, padding: "10px 12px",
              background: platform === p.id ? "var(--rc-gold)" : "transparent",
              color: platform === p.id ? "#000" : "var(--rc-text)",
              border: `1px solid ${platform === p.id ? "var(--rc-gold)" : "var(--rc-gold-dim)"}`,
              borderRadius: 6, cursor: "pointer", fontSize: 12, letterSpacing: 1,
            }}
          >{p.icon} {p.label.toUpperCase()}</button>
        ))}
      </div>

      <label style={{ display: "block", marginBottom: 12 }}>
        <span style={{ fontSize: 10, letterSpacing: 2, color: "var(--rc-text-dim)" }}>CAPTION</span>
        <textarea
          data-testid="social-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={4}
          placeholder="Bold, on-brand. Tag accounts. Add hashtags at the end."
          style={{ width: "100%", marginTop: 4, padding: 10, background: "rgba(0,0,0,0.4)", color: "var(--rc-text)", border: "1px solid var(--rc-gold-dim)", borderRadius: 4, fontFamily: "inherit", fontSize: 13 }}
        />
      </label>

      <label style={{ display: "block", marginBottom: 16 }}>
        <span style={{ fontSize: 10, letterSpacing: 2, color: "var(--rc-text-dim)" }}>
          MEDIA URL {active?.requiresMedia ? "(REQUIRED)" : "(OPTIONAL)"}
        </span>
        <input
          data-testid="social-media-url"
          value={mediaUrl}
          onChange={(e) => setMediaUrl(e.target.value)}
          placeholder="https://... public URL of photo or video"
          style={{ width: "100%", marginTop: 4, padding: 10, background: "rgba(0,0,0,0.4)", color: "var(--rc-text)", border: "1px solid var(--rc-gold-dim)", borderRadius: 4, fontSize: 13 }}
        />
      </label>

      <button
        data-testid="social-fire"
        disabled={busy || !caption || (active?.requiresMedia && !mediaUrl)}
        onClick={fire}
        style={{
          width: "100%", padding: "14px 18px",
          background: "var(--rc-gold)", color: "#000",
          border: "none", borderRadius: 6, fontWeight: 700, fontSize: 14, letterSpacing: 2,
          cursor: busy ? "wait" : "pointer", opacity: (busy || !caption || (active?.requiresMedia && !mediaUrl)) ? 0.5 : 1,
        }}
      >{busy ? "POSTING..." : `→ POST TO ${active?.label.toUpperCase()}`}</button>

      {result && (
        <div data-testid="social-result" style={{ marginTop: 16, padding: 12, background: "rgba(0,0,0,0.4)", border: "1px solid var(--rc-gold-dim)", borderRadius: 4, fontSize: 12, lineHeight: 1.5 }}>
          {result.posted ? (
            <span style={{ color: "var(--rc-gold)" }}>✓ Posted to {result.platform} · ID: {result.post_id}</span>
          ) : result.configured === false ? (
            <span>
              <strong style={{ color: "#ff7e7e" }}>⚠ {result.platform} not connected.</strong><br />
              Missing: <code>{(result.missing_keys || []).join(", ")}</code><br />
              <span style={{ color: "var(--rc-text-dim)" }}>{result.message}</span>
            </span>
          ) : result.error ? (
            <span style={{ color: "#ff7e7e" }}>⚠ {result.error}</span>
          ) : (
            <pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
